import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";
import { useAuthSchedulesQuery } from "apis/authSchedule";

const OVERRIDE_TYPES = [
  { value: "marketingApprove", label: "Marketing Approve" },
  { value: "marketingDraft", label: "Marketing Draft" },
  { value: "marketingPaid", label: "Marketing Paid" },
  { value: "live", label: "Live" },
  { value: "sold", label: "Sold" },
  { value: "compliance", label: "Compliance" },
];

export default function AdminOverrideEdit({ initialData = {}, onSubmit }) {
  const isEdit = !!initialData._id;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      propertyId: initialData.propertyId?._id || "",
      type: initialData.type || "",
      notes: initialData.notes || "",
    },
  });

  useEffect(() => {
    register("propertyId", { required: "Property is required" });
    register("type", { required: "Type is required" });
  }, [register]);

  const { data: schedules = [], isPending: loadingProps } =
    useAuthSchedulesQuery("address,propertyId");

  const propertyOptions = schedules
    .filter((s) => s.address)
    .map((s) => ({
      value: s.propertyId,
      label: s.address,
      // org name + settlement together as small secondary text
      subLabel: [s.organizationName, s.isSettled ? "Settled" : "Active"]
        .filter(Boolean)
        .join(" · "),
      isSettled: s.isSettled,
    }));

  const watchPropertyId = watch("propertyId");
  const watchType = watch("type");

  const selectedProperty =
    propertyOptions.find((o) => o.value === watchPropertyId) || null;
  const selectedType =
    OVERRIDE_TYPES.find((o) => o.value === watchType) || null;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Property */}
      <div className="space-y-2">
        <label className="form-label">Property *</label>
        <SearchSelect
          options={propertyOptions}
          value={selectedProperty}
          onChange={(opt) =>
            setValue("propertyId", opt?.value || "", { shouldValidate: true })
          }
          placeholder="Select a property"
          isLoading={loadingProps}
          isClearable
          disabled={isEdit}
        />

        {/* Show org + settlement for the selected property (survives after select) */}
        {selectedProperty?.subLabel && (
          <p className="text-[10px] text-darkgray mt-1">
            {selectedProperty.subLabel}
          </p>
        )}

        {errors.propertyId && (
          <p className="form-error-message">{errors.propertyId.message}</p>
        )}
        {isEdit && (
          <p className="text-sm text-darkgray">
            Property cannot be changed after creation.
          </p>
        )}
      </div>

      {/* Type */}
      <div className="space-y-2">
        <label className="form-label">Override Type *</label>
        <SearchSelect
          options={OVERRIDE_TYPES}
          value={selectedType}
          onChange={(opt) =>
            setValue("type", opt?.value || "", { shouldValidate: true })
          }
          placeholder="Select a type"
          isClearable
        />
        {errors.type && (
          <p className="form-error-message">{errors.type.message}</p>
        )}
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <label htmlFor="notes" className="form-label">
          Notes *
        </label>
        <textarea
          id="notes"
          rows={4}
          {...register("notes", {
            required: "Notes are required",
            minLength: { value: 3, message: "Please add a brief reason" },
          })}
          className={`form-input border ${errors.notes ? "border-red-500" : "border-mediumgray"}`}
          placeholder="Why is this override being applied?"
        />
        {errors.notes && (
          <p className="form-error-message">{errors.notes.message}</p>
        )}
        <p className="text-sm text-darkgray">
          Required — recorded for audit so others know why this changed.
        </p>
      </div>

      <div className="flex justify-end mt-6 pt-4 border-t border-mediumgray">
        <Button type="submit" className="black-button">
          {isEdit ? "Update Override" : "Create Override"}
        </Button>
      </div>
    </form>
  );
}
